import {
  Accordion,
  AccordionTypes,
  Button,
  CheckableNotice,
  Heading,
  Input,
  Select,
  Text,
  HStack,
  Stack,
  VStack,
} from '@genial/components';
import { Service } from '@genial/services';
import { Utils } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Box } from 'native-base';
import React, { useCallback, useMemo, useState } from 'react';

import { LateralBar } from '../../../../../../../../../../components/LateralBar';
import { useToastCreator } from '../../../../../../../../../../hooks/useToastCreator';
import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../../../../../../../routes/types';
import { ReturnTypeof_InserirPrevidencia } from '../../../../../../../../../../store/product/external/types';
import { usePrivatePensionAssetController } from '../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension';
import { PrivatePensionPaymentMethod } from '../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension/types';
import { TicketApplicationInProgress } from '../../../../components/TicketApplicationInProgress';
import { TicketApplicationSuccessDetails } from '../../../../components/TicketApplicationSuccessDetails';
import { blankTicketApplicationBaseInfo } from '../../../../components/TicketApplicationSuccessDetails/constants';
import { PrivatePensionModalContainer } from '../PrivatePensionModalContainer';
import { PrivatePensionModalHeader } from '../PrivatePensionModalHeader';

export const PrivatePensionOrderSummaryModal: React.FC = () => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [observation, setObservation] = useState('');
  const [customSendingChannel, setCustomSendingChannel] = useState('');
  const [beneficiariesAccordionStatus, setBeneficiariesAccordionStatus] =
    useState<AccordionTypes.Status>('closed');
  const [portabilityAccordionStatus, setPortabilityAccordionStatus] =
    useState<AccordionTypes.Status>('closed');
  const route =
    useRoute<
      RouteProp<SignedInRootStackParamList, 'CustomerDetailPrivatePensionOrderSummaryModal'>
    >();

  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();

  const { offer, accountId, customerId, accountNumber, cpfCnpj, cpfCnpjFormatted } = route.params;
  const offerController = usePrivatePensionAssetController(offer, accountId, accountNumber);
  const { showToast, showToastFromError } = useToastCreator('privatePensionApplicationInCustomer');
  const [sendingOrder, setSendingOrder] = useState(false);
  const [success, setSuccess] = useState(false);

  const {
    accountHolder,
    paymentMethod,
    paymentMethodOptions,
    dueDate,
    bankAccountId,
    bankAccountOptions,
    monthlyContribution,
    singleContribution,
    beneficiaries,
    portabilities,
    relatedPartyOptions,
    generateOrderData,
    legacySendingChannel,
    legacySendingChannelsOptions,
    legacySendingChannelIsOther,
    setLegacySendingChannel,
    hasLegacySendingChannel,
  } = offerController;

  const allowedToSend =
    termsAccepted &&
    hasLegacySendingChannel &&
    (legacySendingChannelIsOther ? !!customSendingChannel.length : true);

  const successInfos = useMemo(
    () => ({
      ...blankTicketApplicationBaseInfo,
      productDetails: [
        {
          label: 'Produto',
          value: offer.productNameNormalized,
        },
        {
          label: 'Valor Total',
          value: Utils.Mask.Money(singleContribution),
        },
        {
          label: 'Data do investimento',
          value: Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy'),
        },
      ],
    }),
    [offer.productNameNormalized, singleContribution]
  );

  const accountHolderFields = [
    {
      label: 'Nome',
      value: accountHolder?.name,
    },
    {
      label: 'Documento',
      value: accountHolder?.cpf,
    },
    {
      label: 'Email',
      value: accountHolder?.email,
    },
  ];

  const paymentFields = [
    {
      label: 'Forma de pagamento',
      value: paymentMethodOptions.find((option) => Number(option.value) === paymentMethod)?.label,
    },
    {
      label: 'Data de vencimento',
      value: dueDate,
    },
    ...(paymentMethod === PrivatePensionPaymentMethod.Debit
      ? [
          {
            label: 'Banco',
            value: bankAccountOptions.find((option) => Number(option.value) === bankAccountId)
              ?.label,
          },
        ]
      : []),
  ];

  const productFields = [
    {
      label: 'Produto',
      value: offer.productNameNormalized,
    },
    {
      label: 'Contribuição mensal',
      value: Utils.Mask.Money(monthlyContribution),
    },
    {
      label: 'Aporte',
      value: Utils.Mask.Money(singleContribution),
    },
  ];

  const beneficiariesFields = beneficiaries.map((beneficiary) => [
    {
      label: 'Nome',
      value: beneficiary.name,
    },
    {
      label: 'Parentesco',
      value: relatedPartyOptions.find(
        (relatedParty) => Number(relatedParty.value) === beneficiary.relatedPartyId
      )?.label,
    },
    {
      label: 'Participação',
      value: Utils.Mask.Percentage(beneficiary.participationPercentage),
    },
  ]);

  const portabilitiesFields = portabilities.map((portability) => [
    {
      label: 'SUSEP',
      value: Utils.Mask.Susep(portability.susepNumber),
    },
    {
      label: 'Valor',
      value: Utils.Mask.Money(portability.value),
    },
  ]);

  const handleBeneficiariesAccordionPress = useCallback(() => {
    setBeneficiariesAccordionStatus((prev) => (prev === 'closed' ? 'opened' : 'closed'));
  }, []);

  const handlePortabilityAccordionPress = useCallback(() => {
    setPortabilityAccordionStatus((prev) => (prev === 'closed' ? 'opened' : 'closed'));
  }, []);

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleMakeOrder = () => {
    setSendingOrder(true);

    const orderData = generateOrderData(observation, legacySendingChannel, customSendingChannel);

    Service.External.Extranet.Previdencia.V1.previdenciaInserirPrevidencia({
      screenName: 'privatePensionApplication',
      solicitacao: orderData,
    })
      .then((response) => {
        const result = response as ReturnTypeof_InserirPrevidencia;

        if (!result.sucesso) {
          showToast('error', 'Erro', result.retorno.map((item) => item).join('\n'));
          return;
        }

        setSuccess(true);
      })
      .catch((error) => {
        setSuccess(false);
        showToastFromError(error);
      })
      .finally(() => {
        setSendingOrder(false);
      });
  };

  const handleCompleteOrder = () => {
    navigation.goBack();
  };

  const handleFollowOrder = () => {
    navigation.replace('AppDrawer', {
      screen: 'CustomerDetailGroup',
      params: {
        screen: 'CustomerDetail',
        params: {
          selectedTab: 'transactions',
          // movementInitialTab: 'PrivatePension',
          customerId,
          accountId,
          accountNumber,
          cpfCnpj,
          cpfCnpjFormatted,
        },
      },
    });
  };

  if (success) {
    return (
      <PrivatePensionModalContainer dismissable={false}>
        <TicketApplicationSuccessDetails
          productDetails={successInfos.productDetails}
          orderDetails={successInfos.orderDetails}
          onClose={handleCompleteOrder}
          onFollow={handleFollowOrder}
          closeButtonText="Nova Aplicação"
          orderHash={successInfos.orderHash}
        />
      </PrivatePensionModalContainer>
    );
  }

  if (sendingOrder) {
    return (
      <PrivatePensionModalContainer dismissable={false}>
        <TicketApplicationInProgress />
      </PrivatePensionModalContainer>
    );
  }

  return (
    <PrivatePensionModalContainer>
      <VStack space="stack.xxxs">
        <PrivatePensionModalHeader
          title="Nova Aplicação - Confirmação de ordem"
          onClose={handleGoBack}
        />
        <Box padding="inset.sm">
          <LateralBar barColor="product.previdencia" />
          <VStack space="stack.xxxs">
            <Heading size="h4-semibold" testId="private-pension-order-summary-modal-heading">
              Resumo da contratação
            </Heading>
            <Text variant="semibold">Responsável</Text>
            {accountHolderFields.map((field) => (
              <HStack alignItems="center" justifyContent="space-between" key={field.label}>
                <Text>{field.label}</Text>
                <Text variant="semibold" textAlign="right">
                  {field.value}
                </Text>
              </HStack>
            ))}

            <Box borderBottomColor="neutral.lightest" borderBottomWidth={1} />
            <Text variant="semibold">Pagamento</Text>
            {paymentFields.map((field) => (
              <HStack alignItems="center" justifyContent="space-between" key={field.label}>
                <Text>{field.label}</Text>
                <Text variant="semibold" textAlign="right">
                  {field.value}
                </Text>
              </HStack>
            ))}

            <Box borderBottomColor="neutral.lightest" borderBottomWidth={1} />
            <Accordion
              status={beneficiariesAccordionStatus}
              onPress={handleBeneficiariesAccordionPress}
              hasShadow={false}>
              <Accordion.Visible containerProps={{ backgroundColor: 'background' }}>
                <Text variant="semibold">Beneficiários</Text>
              </Accordion.Visible>
              <Accordion.Expanded>
                <VStack space="stack.xxxs">
                  {beneficiariesFields.map((beneficiary, index) => (
                    <VStack space="stack.xxxs" key={index.toString()}>
                      {beneficiary.map((field) => (
                        <HStack
                          alignItems="center"
                          justifyContent="space-between"
                          key={field.label}>
                          <Text>{field.label}</Text>
                          <Text variant="semibold" textAlign="right">
                            {field.value}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  ))}
                </VStack>
              </Accordion.Expanded>
            </Accordion>
            <Accordion
              status={portabilityAccordionStatus}
              onPress={handlePortabilityAccordionPress}
              hasShadow={false}>
              <Accordion.Visible containerProps={{ backgroundColor: 'background' }}>
                <Text variant="semibold">Portabilidade</Text>
              </Accordion.Visible>
              <Accordion.Expanded>
                <VStack space="stack.xxxs">
                  {portabilitiesFields.map((portability, index) => (
                    <VStack key={index.toString()} space="stack.xxxs">
                      {portability.map((field) => (
                        <HStack
                          alignItems="center"
                          justifyContent="space-between"
                          key={field.label}>
                          <Text>{field.label}</Text>
                          <Text variant="semibold" textAlign="right">
                            {field.value}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  ))}
                </VStack>
              </Accordion.Expanded>
            </Accordion>

            <Box borderBottomColor="neutral.lightest" borderBottomWidth={1} />
            <Text variant="semibold">Previdência</Text>
            {productFields.map((field) => (
              <HStack alignItems="center" justifyContent="space-between" key={field.label}>
                <Text>{field.label}</Text>
                <Text variant="semibold" textAlign="right">
                  {field.value}
                </Text>
              </HStack>
            ))}
            <HStack
              alignItems="center"
              justifyContent="space-between"
              backgroundColor="background"
              p="inset.xs">
              <Text>Valor total da boleta</Text>
              <Text variant="semibold" textAlign="right">
                {Utils.Mask.Money(singleContribution)}
              </Text>
            </HStack>
            <VStack space="stack.nano">
              <Text variant="semibold">Selecione o canal</Text>
              <Select
                placeholder="Selecione"
                options={legacySendingChannelsOptions}
                selectedValue={legacySendingChannel}
                onValueChange={setLegacySendingChannel}
              />
            </VStack>
            {legacySendingChannelIsOther && (
              <VStack space="stack.nano">
                <Text variant="semibold">Nome do canal</Text>
                <Input
                  placeholder="Digite o nome do outro canal"
                  value={customSendingChannel}
                  onChange={(e) => setCustomSendingChannel(e.nativeEvent.text)}
                  variant="filled"
                  hasShadow={false}
                  name="customSendingChannel"
                  autoComplete="off"
                />
              </VStack>
            )}
            <VStack space="stack.nano">
              <Text variant="semibold">Observação</Text>
              <Input
                placeholder="Existe alguma informação sobre essa autorização"
                value={observation}
                onChange={(e) => setObservation(e.nativeEvent.text)}
                variant="filled"
                hasShadow={false}
                name="observation"
                autoComplete="off"
                numberOfLines={3}
              />
            </VStack>
          </VStack>
        </Box>

        <CheckableNotice
          isSelected={termsAccepted}
          onCheckboxChange={setTermsAccepted}
          textObjects={[
            {
              text: 'Atenção: ',
              variant: 'semibold',
            },
            {
              text: 'Declaro que a solicitação de aplicação foi autorizada pelo cliente, de forma prévia á realização desta operação, e que a mesma foi recebida por canal autorizado e gravado',
              variant: 'regular',
            },
          ]}
        />

        <Stack
          direction={{ base: 'column', md: 'row' }}
          width="100%"
          justifyContent={{ base: 'center', md: 'flex-end' }}
          flexDirection={{ base: 'column-reverse', md: 'row' }}
          space="stack.nano">
          <Button
            isDisabled={sendingOrder}
            variant="secondary"
            onPress={handleGoBack}
            testID="private-pension-order-summary-modal-cancel-button">
            Cancelar
          </Button>
          <Button
            isDisabled={!allowedToSend || sendingOrder}
            testID="private-pension-order-summary-modal-confirm-button"
            onPress={handleMakeOrder}>
            Confirmar
          </Button>
        </Stack>
      </VStack>
    </PrivatePensionModalContainer>
  );
};
